// Workshop API Service
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Helper function to get auth token
const getToken = () => {
  try {
    // Get user object from localStorage (matches the rest of the application)
    const userData = JSON.parse(localStorage.getItem("user"));
    return userData?.token || null;
  } catch (error) {
return null;
  }
};

// Generic API call wrapper with error handling
const apiCall = async (url, options = {}) => {
  try {
    const token = getToken();

    // Log for debugging
const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle 401 errors specifically
      if (response.status === 401) {
// You might want to redirect to login or refresh token here
      }

      // Handle 400 validation errors - show detailed info
      if (response.status === 400) {
if (errorData.errors && Array.isArray(errorData.errors)) {
errorData.errors.forEach((error) => {
});
        }
      }

      throw new Error(
        errorData.message || `API call failed with status ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
throw error;
  }
};

// Enhanced Workshop API with better duplicate prevention
class WorkshopAPI {
  static activeSubmissions = new Set(); // Track currently active submissions
  static lastSubmissionTime = 0; // Track last submission time globally

  static async createWorkshopWithPrevention(workshopData) {
    const userId =
      localStorage.getItem("userId") || localStorage.getItem("user")?.id;
    const now = Date.now();

    // Global rate limiting check
    if (now - this.lastSubmissionTime < 1000) {
      // 1 second global cooldown
throw new Error("Please wait a moment before submitting again.");
    }

    // User-specific concurrent submission check
    const submissionKey = `${userId}-${workshopData.title?.substring(0, 20)}`;
    if (this.activeSubmissions.has(submissionKey)) {
throw new Error("This workshop is already being created. Please wait.");
    }

    // Add to active submissions
    this.activeSubmissions.add(submissionKey);
    this.lastSubmissionTime = now;

    try {
      const token = getToken();
const response = await fetch(`${API_BASE_URL}/workshops`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(workshopData),
      });

      const data = await response.json();

      // 🐛 ENHANCED API DEBUGGING
if (!response.ok) {
        // 🐛 DEBUG: Log detailed error information
// Handle specific error cases
        if (response.status === 429) {
          throw new Error(
            data.message || "Too many requests. Please try again later."
          );
        } else if (response.status === 400) {
          // 🐛 ENHANCED: Include validation details in error message
          const errorDetails = data.errors || data.validationErrors || {};
          const errorMessage = data.message || "Validation failed";
          const detailsString =
            Object.keys(errorDetails).length > 0
              ? `\n\nValidation errors:\n${JSON.stringify(
                  errorDetails,
                  null,
                  2
                )}`
              : "";

          throw new Error(errorMessage + detailsString);
        } else {
          throw new Error(
            data.message || "Failed to create workshop. Please try again."
          );
        }
      }
return data;
    } catch (error) {
throw error;
    } finally {
      // Always remove from active submissions after a delay
      setTimeout(() => {
        this.activeSubmissions.delete(submissionKey);
      }, 2000); // 2 second cleanup delay
    }
  }
}

// Workshop Management APIs (PART 1)
export const workshopApi = {
  // ==================== BASIC WORKSHOP OPERATIONS ====================

  // Get All Workshops (Public)
  // Backend uses 'q' for search query, not 'search'
  getWorkshops: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    // Map filters to backend expected parameters
    // Backend API: GET /api/workshops?q=&category=&field=&format=&pricing=&status=&page=&limit=
    if (filters.query) queryParams.append("q", filters.query);  // Backend uses 'q' not 'search'
    if (filters.q) queryParams.append("q", filters.q);          // Also support 'q' directly
    if (filters.category) queryParams.append("category", filters.category);
    if (filters.field) queryParams.append("field", filters.field);
    if (filters.format) queryParams.append("format", filters.format);
    if (filters.pricing) queryParams.append("pricing", filters.pricing);
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);
    // Note: sortBy and sortOrder are not in the backend spec but keeping for compatibility
    if (filters.sortBy) queryParams.append("sortBy", filters.sortBy);
    if (filters.sortOrder) queryParams.append("sortOrder", filters.sortOrder);

    return await apiCall(`/workshops?${queryParams}`);
  },

  // Get Workshop by ID
  getWorkshopById: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}`);
  },

  // Create Workshop with enhanced duplicate prevention
  createWorkshop: async (workshopData) => {
    // Use the enhanced version for create operations
    return await WorkshopAPI.createWorkshopWithPrevention(workshopData);
  },

  // Update Workshop
  updateWorkshop: async (workshopId, updateData) => {
    return await apiCall(`/workshops/${workshopId}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
  },

  // Update Workshop Schedule (dedicated endpoint for schedule management)
  // Allows organizers AND co-organizers with canManageSchedule permission
  updateSchedule: async (workshopId, scheduleData) => {
    return await apiCall(`/workshops/${workshopId}/schedule`, {
      method: "PUT",
      body: JSON.stringify({ schedule: scheduleData }),
    });
  },

  // Publish Workshop
  publishWorkshop: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}`, {
      method: "PUT",
      body: JSON.stringify({
        status: "published",
      }),
    });
  },

  // Delete Workshop
  deleteWorkshop: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}`, {
      method: "DELETE",
    });
  },

  // Get User's Workshops
  getMyWorkshops: async (role = "all", status = "all") => {
    const queryParams = new URLSearchParams();
    if (role !== "all") queryParams.append("role", role);
    if (status !== "all") queryParams.append("status", status);

    return await apiCall(`/workshops/my-workshops?${queryParams}`);
  },

  // Get Featured Workshops
  getFeaturedWorkshops: async (limit = 6) => {
    return await apiCall(`/workshops/featured?limit=${limit}`);
  },

  // Get Workshop Categories
  getWorkshopCategories: async () => {
    return await apiCall("/workshops/categories");
  },

  // Search Workshops
  searchWorkshops: async (searchTerm, filters = {}) => {
    const queryParams = new URLSearchParams({
      search: searchTerm,
      ...filters,
    });

    return await apiCall(`/workshops/search?${queryParams}`);
  },

  // Get User's Application History
  getMyApplicationHistory: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);
    if (filters.status) queryParams.append("status", filters.status);

    // ✅ NEW: Include complete history as per backend engineer's specification
    queryParams.append("includeHistory", "true");

    return await apiCall(`/workshops/my-applications?${queryParams}`);
  },

  // ✅ NEW: Get detailed application history for specific workshop
  getMyWorkshopApplicationHistory: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/my-application-history`);
  },

  // ✅ NEW: Edit pending application
  editMyApplication: async (workshopId, applicationData) => {
    return await apiCall(`/workshops/${workshopId}/my-application`, {
      method: "PUT",
      body: JSON.stringify(applicationData),
    });
  },

  // ✅ NEW: Get user's current application for specific workshop
  getMyCurrentApplication: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/my-application`);
  },

  // ✅ NEW: Get signed URL for payment proof image
  getPaymentProofSignedUrl: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/payment-proof-view-url`);
  },

  // ==================== PARTICIPANT MANAGEMENT (PART 2) ====================

  // Apply to Workshop
  applyToWorkshop: async (workshopId, applicationData = {}) => {
    return await apiCall(`/workshops/${workshopId}/apply`, {
      method: "POST",
      body: JSON.stringify(applicationData),
    });
  },

  // Get Workshop Participants (Organizer Only)
  getWorkshopParticipants: async (workshopId, filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.status) queryParams.append("status", filters.status);
    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);

    return await apiCall(
      `/workshops/${workshopId}/participants?${queryParams}`
    );
  },

  // Approve Participant Application
  approveParticipantApplication: async (
    workshopId,
    userId,
    approvalData = {}
  ) => {
    return await apiCall(
      `/workshops/${workshopId}/participants/${userId}/approve`,
      {
        method: "POST",
        body: JSON.stringify(approvalData),
      }
    );
  },

  // Reject Participant Application
  rejectParticipantApplication: async (workshopId, userId, rejectionData) => {
    return await apiCall(
      `/workshops/${workshopId}/participants/${userId}/reject`,
      {
        method: "POST",
        body: JSON.stringify(rejectionData),
      }
    );
  },

  // Remove Participant
  removeParticipant: async (workshopId, userId, removalData = {}) => {
    const token = getToken();
return await apiCall(`/workshops/${workshopId}/participants/${userId}`, {
      method: "DELETE",
      body: JSON.stringify(removalData),
    });
  },

  // Bulk Approve Participants
  bulkApproveParticipants: async (workshopId, userIds) => {
    return await apiCall(`/workshops/${workshopId}/participants/bulk-approve`, {
      method: "POST",
      body: JSON.stringify({ userIds }),
    });
  },

  // ==================== PAYMENT PROCESSING (PART 2) ====================

  // Generate Payment Proof Upload URL
  generatePaymentProofUploadUrl: async (workshopId, filename, contentType) => {
    return await apiCall(`/workshops/${workshopId}/payment-proof-upload-url`, {
      method: "POST",
      body: JSON.stringify({
        filename,
        contentType,
      }),
    });
  },

  // Upload Payment Proof File
  uploadPaymentProofFile: async (uploadUrl, file) => {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });
    return response.ok;
  },

  // Submit Payment Proof
  submitPaymentProof: async (
    workshopId,
    imageKey,
    amount,
    paymentMethod,
    transactionId,
    additionalNotes
  ) => {
    return await apiCall(`/workshops/${workshopId}/payment-proof`, {
      method: "POST",
      body: JSON.stringify({
        imageKey,
        amount: amount || 0,
        paymentMethod: paymentMethod || "bank_transfer",
        transactionId: transactionId || "",
        additionalNotes: additionalNotes || "",
      }),
    });
  },

  // Approve Payment
  approvePayment: async (workshopId, userId, approvalData = {}) => {
    return await apiCall(
      `/workshops/${workshopId}/participants/${userId}/approve-payment`,
      {
        method: "POST",
        body: JSON.stringify(approvalData),
      }
    );
  },

  // Reject Payment
  rejectPayment: async (workshopId, userId, rejectionData) => {
    return await apiCall(
      `/workshops/${workshopId}/participants/${userId}/reject-payment`,
      {
        method: "POST",
        body: JSON.stringify(rejectionData),
      }
    );
  },

  // Get Workshop Statistics (Organizer)
  getWorkshopStatistics: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/statistics`);
  },

  // Export Participants (Organizer)
  exportParticipants: async (workshopId, format = "csv") => {
    const response = await fetch(
      `${API_BASE_URL}/workshops/${workshopId}/participants/export?format=${format}`,
      {
        headers: { Authorization: `Bearer ${getToken()}` },
      }
    );

    // Handle file download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workshop-${workshopId}-participants.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true };
  },

  // ==================== DISCUSSIONS & Q&A (PART 3) ====================

  // Get Workshop Discussions
  getWorkshopDiscussions: async (workshopId, filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.type) queryParams.append("type", filters.type);
    if (filters.sessionDay)
      queryParams.append("sessionDay", filters.sessionDay);
    if (filters.search) queryParams.append("search", filters.search);
    if (filters.sortBy) queryParams.append("sortBy", filters.sortBy);
    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);

    return await apiCall(`/workshops/${workshopId}/discussions?${queryParams}`);
  },

  // Create Discussion/Question
  createDiscussion: async (workshopId, discussionData) => {
    return await apiCall(`/workshops/${workshopId}/discussions`, {
      method: "POST",
      body: JSON.stringify(discussionData),
    });
  },

  // Reply to Discussion
  replyToDiscussion: async (workshopId, discussionId, replyData) => {
    return await apiCall(
      `/workshops/${workshopId}/discussions/${discussionId}/replies`,
      {
        method: "POST",
        body: JSON.stringify(replyData),
      }
    );
  },

  // Upvote Discussion
  upvoteDiscussion: async (workshopId, discussionId) => {
    return await apiCall(
      `/workshops/${workshopId}/discussions/${discussionId}/upvote`,
      {
        method: "POST",
      }
    );
  },

  // Upvote Reply
  upvoteReply: async (workshopId, discussionId, replyId) => {
    return await apiCall(
      `/workshops/${workshopId}/discussions/${discussionId}/replies/${replyId}/upvote`,
      {
        method: "POST",
      }
    );
  },

  // Mark Question as Resolved (Organizer)
  markQuestionAsResolved: async (workshopId, discussionId) => {
    return await apiCall(
      `/workshops/${workshopId}/discussions/${discussionId}/resolve`,
      {
        method: "POST",
      }
    );
  },

  // ==================== WORKSHOP RESOURCES (PART 3) ====================

  // Get Workshop Resources
  getWorkshopResources: async (workshopId, filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.type) queryParams.append("type", filters.type);
    if (filters.sessionDay)
      queryParams.append("sessionDay", filters.sessionDay);
    if (filters.search) queryParams.append("search", filters.search);

    return await apiCall(`/workshops/${workshopId}/resources?${queryParams}`);
  },

  // Upload Resource (Organizer)
  uploadWorkshopResource: async (workshopId, resourceData) => {
    return await apiCall(`/workshops/${workshopId}/resources`, {
      method: "POST",
      body: JSON.stringify(resourceData),
    });
  },

  // Delete Resource (Organizer)
  deleteWorkshopResource: async (workshopId, resourceId) => {
    return await apiCall(`/workshops/${workshopId}/resources/${resourceId}`, {
      method: "DELETE",
    });
  },

  // Bulk Delete Resources (Organizer)
  bulkDeleteResources: async (workshopId, resourceIds) => {
    return await apiCall(`/workshops/${workshopId}/resources/bulk-delete`, {
      method: "POST",
      body: JSON.stringify({ resourceIds }),
    });
  },

  // ==================== ANNOUNCEMENTS (PART 3) ====================

  // Get Workshop Announcements
  getWorkshopAnnouncements: async (workshopId, filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);

    return await apiCall(
      `/workshops/${workshopId}/announcements?${queryParams}`
    );
  },

  // Create Announcement (Organizer)
  createAnnouncement: async (workshopId, announcementData) => {
    return await apiCall(`/workshops/${workshopId}/announcements`, {
      method: "POST",
      body: JSON.stringify(announcementData),
    });
  },

  // Send Urgent Announcement (Organizer)
  sendUrgentAnnouncement: async (workshopId, announcementData) => {
    return await apiCall(`/workshops/${workshopId}/announcements/urgent`, {
      method: "POST",
      body: JSON.stringify(announcementData),
    });
  },

  // ==================== NOTIFICATIONS (PART 4) ====================

  // Get User Notifications
  getUserNotifications: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.read !== undefined) queryParams.append("read", filters.read);
    if (filters.type) queryParams.append("type", filters.type);
    if (filters.priority) queryParams.append("priority", filters.priority);
    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);
    if (filters.createdAfter)
      queryParams.append("createdAfter", filters.createdAfter);

    return await apiCall(`/notifications?${queryParams}`);
  },

  // Mark Notification as Read
  markNotificationAsRead: async (notificationId) => {
    return await apiCall(`/notifications/${notificationId}/read`, {
      method: "PUT",
    });
  },

  // Mark All Notifications as Read
  markAllNotificationsAsRead: async () => {
    return await apiCall("/notifications/mark-all-read", {
      method: "PUT",
    });
  },

  // Get Unread Notification Count
  getUnreadNotificationCount: async () => {
    return await apiCall("/notifications/unread-count");
  },

  // ==================== CO-ORGANIZER MANAGEMENT (PART 4) ====================

  // Search Users for Co-Organizer (Organizer Only)
  // Returns users excluding current organizer and existing co-organizers
  searchUsersForCoOrganizer: async (workshopId, searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      return { success: true, data: { users: [] } };
    }
    return await apiCall(
      `/workshops/${workshopId}/co-organizers/search?q=${encodeURIComponent(searchQuery)}`
    );
  },

  // Get Co-Organizers List
  // Returns full details for organizer, limited info for others
  getCoOrganizers: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/co-organizers`);
  },

  // Add Co-organizer (Organizer Only)
  // Maximum 5 co-organizers per workshop
  addCoOrganizer: async (workshopId, coOrganizerData) => {
    // coOrganizerData: { userId, permissions: { canApproveParticipants, canUploadResources, canSendAnnouncements, canManageSchedule } }
    return await apiCall(`/workshops/${workshopId}/co-organizers`, {
      method: "POST",
      body: JSON.stringify(coOrganizerData),
    });
  },

  // Update Co-organizer Permissions (Organizer Only)
  // Uses PATCH method as per backend spec
  updateCoOrganizerPermissions: async (workshopId, userId, permissions) => {
    return await apiCall(
      `/workshops/${workshopId}/co-organizers/${userId}/permissions`,
      {
        method: "PATCH",
        body: JSON.stringify({ permissions }),
      }
    );
  },

  // Remove Co-organizer (Organizer Only)
  removeCoOrganizer: async (workshopId, userId) => {
    return await apiCall(`/workshops/${workshopId}/co-organizers/${userId}`, {
      method: "DELETE",
    });
  },

  // Leave as Co-Organizer (Self - Co-organizer Only)
  // Allows co-organizer to voluntarily leave the workshop
  leaveAsCoOrganizer: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/co-organizers/leave`, {
      method: "POST",
    });
  },

  // ==================== SCHEDULE MANAGEMENT (PART 4) ====================

  // Update Workshop Schedule
  updateWorkshopSchedule: async (workshopId, scheduleData) => {
    return await apiCall(`/workshops/${workshopId}/schedule`, {
      method: "PUT",
      body: JSON.stringify(scheduleData),
    });
  },

  // ✅ NEW: Update Payment Instructions
  updatePaymentInstructions: async (workshopId, paymentInstructions) => {
    return await apiCall(`/workshops/${workshopId}/payment-instructions`, {
      method: "PUT",
      body: JSON.stringify({ paymentInstructions }),
    });
  },

  // ==================== LEGACY METHODS (Keep for backward compatibility) ====================

  // Legacy methods (keeping existing names for compatibility)
  approveParticipant: async (workshopId, participantId) => {
    return workshopApi.approveParticipantApplication(workshopId, participantId);
  },

  rejectParticipant: async (workshopId, participantId, reason) => {
    return workshopApi.rejectParticipantApplication(workshopId, participantId, {
      reason,
    });
  },

  getWorkshopStats: async () => {
    return await apiCall("/workshops/stats");
  },

  // ==================== NEW: JOIN BY CODE, FEEDBACK & JOIN CODE ====================

  // Join Workshop by Invite Code
  // Users can join a workshop directly using an invite code shared by organizers
  joinByCode: async (joinCode) => {
    return await apiCall('/workshops/join-by-code', {
      method: 'POST',
      body: JSON.stringify({ joinCode }),
    });
  },

  // Submit Workshop Feedback/Rating
  // Participants can rate and review completed workshops
  submitFeedback: async (workshopId, feedbackData) => {
    // feedbackData: { rating: 1-5, comment: "optional feedback text" }
    return await apiCall(`/workshops/${workshopId}/feedback`, {
      method: 'POST',
      body: JSON.stringify(feedbackData),
    });
  },

  // Get Workshop Join Code (Organizer Only)
  // Returns the invite code and share text for organizers
  getJoinCode: async (workshopId) => {
    return await apiCall(`/workshops/${workshopId}/join-code`);
  },
};

// ==================== HELPER FUNCTIONS ====================

// Apply to workshop with payment proof (for paid workshops)
export const applyToWorkshopWithPayment = async (
  workshopId,
  paymentFile,
  paymentData = {},
  applicationData = {}
) => {
  try {
// First, apply to the workshop
const applicationResponse = await workshopApi.applyToWorkshop(
      workshopId,
      applicationData
    );

    if (!applicationResponse.success) {
      throw new Error("Failed to submit workshop application");
    }
// Generate upload URL for payment proof
const { data: uploadData } =
      await workshopApi.generatePaymentProofUploadUrl(
        workshopId,
        paymentFile.name,
        paymentFile.type
      );
// Upload file to S3
await fetch(uploadData.uploadUrl, {
      method: "PUT",
      body: paymentFile,
      headers: {
        "Content-Type": paymentFile.type,
      },
    });
// Submit payment proof with metadata
const paymentResponse = await workshopApi.submitPaymentProof(
      workshopId,
      uploadData.key,
      paymentData.amount || 0,
      paymentData.paymentMethod || "bank_transfer",
      paymentData.transactionId || "",
      paymentData.notes || paymentData.additionalNotes || ""
    );
return paymentResponse;
  } catch (error) {
throw error;
  }
};

// Complete payment proof upload flow
export const uploadPaymentProof = async (
  workshopId,
  paymentFile,
  additionalData = {}
) => {
  try {
// Generate upload URL
const { data: uploadData } =
      await workshopApi.generatePaymentProofUploadUrl(
        workshopId,
        paymentFile.name,
        paymentFile.type
      );
// Upload file to S3
await fetch(uploadData.uploadUrl, {
      method: "PUT",
      body: paymentFile,
      headers: {
        "Content-Type": paymentFile.type,
      },
    });
// Submit payment proof with metadata
const response = await workshopApi.submitPaymentProof(
      workshopId,
      uploadData.key,
      additionalData.amount || 0,
      additionalData.paymentMethod || "bank_transfer",
      additionalData.transactionId || "",
      additionalData.notes || additionalData.additionalNotes || ""
    );
return response;
  } catch (error) {
throw error;
  }
};

// Complete payment proof upload flow (legacy - keep for compatibility)
export const handlePaymentProofUpload = async (
  workshopId,
  file,
  amount,
  transactionId
) => {
  return uploadPaymentProof(workshopId, file, {
    amount,
    paymentMethod: "bank_transfer",
    transactionId,
    notes: `Amount: ${amount}`,
  });
};
